import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  fetchStats, fetchSnipes, fetchLogs, fetchStatus,
} from "@/lib/api";
import Sidebar from "@/components/dashboard/Sidebar";
import StatsRow from "@/components/dashboard/StatsRow";
import WatchlistTable from "@/components/dashboard/WatchlistTable";
import ScheduleForm from "@/components/dashboard/ScheduleForm";
import InstantRegister from "@/components/dashboard/InstantRegister";
import ActivityLog from "@/components/dashboard/ActivityLog";
import ReadinessBanner from "@/components/dashboard/ReadinessBanner";
import { useBranding } from "@/lib/branding";

export default function Dashboard() {
  const brand = useBranding();
  const [stats, setStats] = useState({ total: 0, scheduled: 0, executing: 0, success: 0, failed: 0, success_rate: 0 });
  const [snipes, setSnipes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState({ mode: "live", ready_to_register: false, transip_configured: false, spaceship_configured: false, contact_configured: false });
  const lastLogIdRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [s, sn, lg, st] = await Promise.all([fetchStats(), fetchSnipes(), fetchLogs(60), fetchStatus()]);
      setStats(s);
      setSnipes(sn);
      setStatus(st);
      // Detect new logs → toast notification
      if (lg.length && lastLogIdRef.current !== null) {
        const newest = lg[0];
        if (newest.id !== lastLogIdRef.current) {
          const idxOld = lg.findIndex((l) => l.id === lastLogIdRef.current);
          const fresh = idxOld === -1 ? lg.slice(0, 3) : lg.slice(0, idxOld);
          for (const l of fresh) {
            if (l.level === "success") toast.success(l.message);
            else if (l.level === "error") toast.error(l.message);
            else if (l.level === "warning") toast.warning(l.message);
            else toast(l.message);
          }
        }
      }
      if (lg.length) lastLogIdRef.current = lg[0].id;
      setLogs(lg);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 2000);
    return () => clearInterval(iv);
  }, [refresh]);

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="dashboard-root">
      <Sidebar status={status} />
      <main className="flex-1 flex min-w-0">
        {/* Center column */}
        <section className="flex-1 flex flex-col min-w-0 border-l border-r border-[#eaecf0]">
          <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="overline">{brand.brand_name}</span>
              <span className="text-[#D4D4D8]">/</span>
              <span className="text-sm font-medium">Control Room</span>
              <span
                data-testid="mode-badge"
                className="ml-2 text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-sm"
                style={
                  status.ready_to_register
                    ? { background: "#ECFDF5", color: "#047857" }
                    : { background: "#FEF3C7", color: "#B45309" }
                }
                title={
                  status.ready_to_register
                    ? "TransIP credentials + contact profile are ready for real registrations"
                    : "Real engine — but TransIP token and/or contact profile still need setup"
                }
              >
                {status.ready_to_register ? "LIVE · READY" : "LIVE · SETUP NEEDED"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 engine-dot" />
              <span className="text-[#525252]">ENGINE ACTIVE</span>
            </div>
          </header>
          <ReadinessBanner status={status} />
          <StatsRow stats={stats} />
          <WatchlistTable snipes={snipes} onChange={refresh} />
        </section>

        {/* Right panel */}
        <aside className="w-[380px] shrink-0 flex flex-col">
          <InstantRegister onDone={refresh} />
          <ScheduleForm onCreated={refresh} />
          <ActivityLog logs={logs} onCleared={refresh} />
        </aside>
      </main>
    </div>
  );
}
